///
/// EarthBrowser
///
/// Created by Matt Giger on 10/27/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Platform detection for the Capacitor shells (earthbrowser-mobile). This
/// is the only place that asks Capacitor which platform we're on; the rest
/// of the app goes through these helpers so the web build never branches
/// on Capacitor internals.
///

import { Capacitor } from '@capacitor/core'

export type NativePlatform = 'ios' | 'android'
export type AppPlatform = 'web' | 'tauri' | NativePlatform

/** True inside the iOS or Android Capacitor shell (never in a browser) */
export function isNativeApp(): boolean {
	return Capacitor.isNativePlatform()
}

/** Which native shell is running, or null on the web */
export function nativePlatform(): NativePlatform | null {
	const platform = Capacitor.getPlatform()
	return platform === 'ios' || platform === 'android' ? platform : null
}

/** Build-time marker set by `bun run build:native` (VITE_BUILD_TARGET=native) */
export function isNativeBuild(): boolean {
	return import.meta.env.VITE_BUILD_TARGET === 'native'
}

/**
 * Whether the Stripe checkout flow may be offered. Store policy requires
 * digital subscriptions bought inside the native apps to go through
 * StoreKit / Play Billing, so the web checkout is hidden there until the
 * in-app purchase integration lands. Web-purchased subscriptions still
 * unlock the app on every platform (entitlement comes from the account).
 */
export function canPurchaseInApp(): boolean {
	return !isNativeApp()
}

/** Whether a Capacitor plugin's native side is present (e.g. 'PushNotifications') */
export function hasNativePlugin(name: string): boolean {
	return isNativeApp() && Capacitor.isPluginAvailable(name)
}
